import { useGetFaireAcademyPageState } from "@faire/retailer-visitor-shared/hooks/openWithFaire/useGetFaireAcademyPageState";
import { useIsLoggedInRetailer } from "@faire/retailer-visitor-shared/lib/isLoggedInRetailer";
import { useGuideStore } from "@faire/retailer-visitor-shared/views/open-with-faire/academy/guidePages/useGuideStore";
import {
  ErrorMessages,
  getErrorMessage,
  getWebApiErrorMessage,
} from "@faire/web--source/common/getErrorMessage";
import { useStrictLocalization } from "@faire/web--source/common/localization";
import { Button } from "@faire/web--source/slate/Button";
import { TextArea } from "@faire/web--source/slate/Input/TextArea";
import { Modal } from "@faire/web--source/slate/Modal";
import { getSpacing, Spacer } from "@faire/web--source/slate/spacing";
import { Typography } from "@faire/web--source/slate/Typography";
import { StrictLocalMsg } from "@faire/web--source/ui/lib/localization";
import { useMutation as useCreateTicket } from "@faire/web-api--source/endpoints/www/api/zendesk/create-ticket/post-hooks";
import { ICreateTicketRequest } from "@faire/web-api--source/com/faire/vendor/zendesk/ICreateTicketRequest";
import { trackOpenWithFaireFaireAcademyNegativeFeedbackDismissClick } from "@faire/web-api--source/events/openWithFaire/click/faireAcademyNegativeFeedbackDismiss";
import { trackOpenWithFaireFaireAcademyNegativeFeedbackSubmitClick } from "@faire/web-api--source/events/openWithFaire/click/faireAcademyNegativeFeedbackSubmit";
import { IFaireAcademyChapter } from "@faire/web-api--source/indigofair/data/IFaireAcademyChapter";
import React, { useState } from "react";
import styled from "styled-components";

export const NegativeFeedbackModal: React.FC<{
  chapterId: IFaireAcademyChapter.ChapterIdentifier;
  closeModal: () => void;
}> = ({ chapterId, closeModal }) => {
  const { pageState } = useGetFaireAcademyPageState();
  const { strictLocalize } = useStrictLocalization();
  const [{ guide }] = useGuideStore(["guide"]);
  const isLoggedInRetailer = useIsLoggedInRetailer();
  const { mutateAsync: createTicket } = useCreateTicket();
  const [feedback, setFeedback] = useState<string>("");
  const [errorMessage, setErrorMessage] = useState<string | undefined>(
    undefined
  );

  const handleSendFeedback = async () => {
    trackOpenWithFaireFaireAcademyNegativeFeedbackSubmitClick(
      pageState,
      guide.id,
      chapterId
    );
    const request = ICreateTicketRequest.build({
      subject: "Faire Academy Chapter Feedback",
      issue_type: "Faire Academy Chapter Feedback",
      description: `Feedback submitted for Guide: ${guide.id}, Chapter: ${chapterId}`,
      comment_body: feedback,
      user_type_tag: isLoggedInRetailer ? "retailer" : "logged_out_user",
      platform: "web",
    });
    try {
      setErrorMessage(undefined);
      await createTicket([request]);
      closeModal();
    } catch (error) {
      const errorMessageOrDefault = getWebApiErrorMessage(
        error,
        strictLocalize,
        getErrorMessage(
          strictLocalize,
          ErrorMessages.PLEASE_REFRESH_AND_TRY_AGAIN
        )
      );
      setErrorMessage(errorMessageOrDefault);
    }
  };

  const closeModalDismiss = () => {
    trackOpenWithFaireFaireAcademyNegativeFeedbackDismissClick(
      pageState,
      guide.id,
      chapterId
    );
    closeModal();
  };

  return (
    <Modal
      padding={getSpacing("10x", "3x", "4x")}
      onClose={closeModalDismiss}
      width="360px"
      align="flex-start"
    >
      <Typography variant="pageHeaderSerifRegular">
        <StrictLocalMsg
          defaultMessage="We’re sorry it wasn’t helpful"
          description={{
            text: "Header above feedback collection",
            img: "https://cdn.faire.com/fastly/b312a9da067661dc4f2d7a89682d3a5225a9ed248b58e90ca5ac49605143ae39.png",
          }}
        />
      </Typography>
      <TextAreaWrapper>
        <FixedHeightTextArea
          label={strictLocalize({
            defaultMessage: "How can we improve?",
            description: {
              text: "Label for text area to leave feedback",
              img: "https://cdn.faire.com/fastly/b312a9da067661dc4f2d7a89682d3a5225a9ed248b58e90ca5ac49605143ae39.png",
            },
          })}
          value={feedback}
          onChange={(e) => {
            setFeedback(e.target.value);
          }}
          error={errorMessage}
        />
      </TextAreaWrapper>
      <FullWidthButton
        onClick={handleSendFeedback}
        disabled={feedback.length < 5}
      >
        <StrictLocalMsg
          defaultMessage="Submit feedback"
          description={{
            text: "Button to submit feedback",
            img: "https://cdn.faire.com/fastly/b312a9da067661dc4f2d7a89682d3a5225a9ed248b58e90ca5ac49605143ae39.png",
          }}
        />
      </FullWidthButton>
      <Spacer height="2x" />
      <FullWidthButton onClick={closeModalDismiss} variant="secondary">
        <StrictLocalMsg
          defaultMessage="Dismiss"
          description={{
            text: "Button to dismiss a modal",
            img: "https://cdn.faire.com/fastly/b312a9da067661dc4f2d7a89682d3a5225a9ed248b58e90ca5ac49605143ae39.png",
          }}
        />
      </FullWidthButton>
    </Modal>
  );
};

const TextAreaWrapper = styled.div`
  width: 100%;
  padding: ${getSpacing("4x", 0)};
`;

const FixedHeightTextArea = styled(TextArea)`
  height: 140px;
`;

const FullWidthButton = styled(Button)`
  width: 100%;
`;
